import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { cn } from '@theprintbar/shared';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useField } from 'remix-validated-form';
import { FieldProps } from '../index';

interface NumberInputProps extends FieldProps<HTMLInputElement> {
	defaultValue?: number;
	value?: number;
	noValidate?: boolean;
	hideLabel?: boolean;
	handleUpdate?: (quantity: number) => void;
	min: number;
	max?: number;
}

export function NumberInput({
	defaultValue,
	hideLabel,
	noValidate,
	handleUpdate,
	name,
	fieldRef,
	min = 0,
	max,
	className,
	value,
	disabled,
	...props
}: NumberInputProps) {
	const { getInputProps } = noValidate ? { getInputProps: () => ({}) } : useField(name);
	const [currentValue, setCurrentValue] = useState(value || defaultValue || min);

	useEffect(() => {
		if (typeof value !== undefined && value !== currentValue) {
			setCurrentValue(value!);
		}
	}, [value]);

	const handleClick = useCallback(
		(change: number) => {
			if ((change < 0 && currentValue + change < min) || (change > 0 && (!max || currentValue + change > max))) return;
			setCurrentValue(currentValue + change);
		},
		[currentValue],
	);

	useEffect(() => {
		if (handleUpdate) {
			handleUpdate(currentValue);
		}
	}, [currentValue]);

	return (
		<div className={cn('flex flex-col', className)}>
			{!hideLabel && (
				<label htmlFor={name} className="font-belfast font-bold text-xs mb-3">
					QUANTITY
				</label>
			)}

			<div className="relative flex justify-around flex-1">
				<button
					disabled={disabled || currentValue <= min}
					type="button"
					className="absolute left-2 inset-y-0 cursor-pointer outline-none disabled:opacity-20 disabled:cursor-default"
					onClick={() => handleClick(-1)}
				>
					<MinusIcon className="h-4 w-4" />
				</button>
				<input
					className={clsx(
						'h-full w-full border-none shadow-none bg-none bg-transparent outline-none focus:ring-0 px-1 p-0 text-center',
						'[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
					)}
					ref={fieldRef}
					name={name}
					type="number"
					id={name}
					{...getInputProps({
						...props,
					})}
					value={currentValue}
					min={min}
					max={max}
					onChange={e => {
						//Ensure the value is a number
						const value = parseInt(e.target.value, 10);
						if (isNaN(value) || value < min || (max && value > max)) return;
						setCurrentValue(parseInt(e.target.value, 10));
					}}
				/>
				<button
					type="button"
					disabled={disabled || (!!max && currentValue >= max)}
					className="absolute right-2 inset-y-0 cursor-pointer outline-none disabled:opacity-20 disabled:cursor-default"
					onClick={() => handleClick(1)}
				>
					<PlusIcon className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}
